/**
 * TransferList Custom Hooks
 *
 * This file contains custom React hooks used by the TransferList component.
 */

import { usePagination } from "@mantine/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";

import type { TransferListItem, TransferListPaginationConfig } from "./TransferList.types";

/**
 * Custom hook to manage pagination state for a TransferList.
 * Wraps Mantine's usePagination with TransferList-specific logic.
 *
 * @template T - The type of items in the list
 * @param items - Array of items to paginate (typically filtered items)
 * @param config - Pagination configuration or null if pagination is disabled
 * @param listSide - Which list this is ("left" or "right") for callbacks
 * @returns Pagination state and controls
 *
 * @example
 * const pagination = useTransferListPagination(
 *   filteredItems,
 *   { pageSize: 20, pageSizeOptions: [10, 20, 50] },
 *   "left"
 * );
 *
 * // Use pagination.paginatedItems for rendering
 * // Use pagination.setPage, pagination.next, pagination.previous for navigation
 */
export function useTransferListPagination<T = TransferListItem>(items: T[], config: TransferListPaginationConfig | null, listSide: "left" | "right") {
  // Manage page size state
  const [pageSize, setPageSize] = useState(config?.pageSize || 10);

  // Calculate pagination values
  const totalItems = items.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  // Use Mantine's usePagination hook for page navigation
  const pagination = usePagination({
    total: totalPages,
    initialPage: 1
  });

  // Calculate paginated items based on current page and page size
  const paginatedItems = useMemo(() => {
    if (!config) {
      return items; // No pagination - return all items
    }

    const startIndex = (pagination.active - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex);
  }, [items, pagination.active, pageSize, config]);

  // Wrap setPage to call callback before state update
  const setPageWithCallback = useCallback(
    (page: number) => {
      // Call callback BEFORE state update
      config?.onPageChange?.(listSide, page, pageSize);
      // Then update the page
      pagination.setPage(page);
    },
    [config, listSide, pageSize, pagination]
  );

  // Function to change page size and reset to first page
  const changePageSize = useCallback(
    (newSize: number) => {
      // Call callback BEFORE state updates
      config?.onPageSizeChange?.(listSide, newSize, 1);
      // Then update the state
      setPageSize(newSize);
      pagination.setPage(1); // Reset to first page
    },
    [config, listSide, pagination]
  );

  // Reset to first page when total items change (e.g., after search)
  useEffect(() => {
    pagination.setPage(1);
  }, [totalItems]);

  // Calculate start and end indices for display (1-indexed for user display)
  const startIndex = totalItems === 0 ? 0 : (pagination.active - 1) * pageSize + 1;
  const endIndex = Math.min(pagination.active * pageSize, totalItems);

  return {
    // State from Mantine's usePagination hook (excluding setPage which we override)
    active: pagination.active,
    range: pagination.range,
    next: pagination.next,
    previous: pagination.previous,
    first: pagination.first,
    last: pagination.last,
    // Override setPage with our callback-aware version
    setPage: setPageWithCallback,

    // Additional state
    pageSize,
    totalItems,
    totalPages,
    paginatedItems,

    // Actions
    changePageSize,

    // Computed values for display
    startIndex,
    endIndex
  };
}
